import React from 'react';

import { AnswerScope } from '../../../../../../../../store/globalAskAica/answerScopeTypes';
import { answerScopeExcludedCopy, GLOBAL_ASK_AICA_COPY } from '../../../../../../../../store/globalAskAica/constants';

import styles from './AnswerScopeLine.module.scss';

interface AnswerScopeLineProps {
    answerScope: AnswerScope;
    /** Pins a covered company for the next turn. Omitted → chips render as plain
     * text, which is the honest thing when nothing can act on a tap. */
    onPinCompany?: (orgId: string) => void;
    /** Blocks a pin while a turn is already streaming — a pin IS a turn, and the
     * hook's own in-flight guard would silently drop it. */
    disabled?: boolean;
}

/**
 * "Answering about: ACC INDIA PRIVATE LIMITED · SAMARA RETAIL" plus a sentence per
 * name the answer could NOT cover — frozen contract v4 §7, read off
 * message.metadata.answer_scope on an assistant turn.
 *
 * Why it exists: a multi-company turn never moves the pin (only a `switch` label
 * does), so without this the user cannot tell WHICH companies an answer covered,
 * and a name that was silently dropped looks like it was answered. The covered
 * companies render as TAPPABLE chips because that is how a multi-company turn hands
 * control back — the backend deliberately never guesses which one to pin.
 *
 * Excluded names are text, never chips: there is no org_id for them by definition,
 * so there is nothing to pin.
 *
 * ⚠️ Live-turn only. The conversation-history response does not carry answer_scope,
 * so this line does not survive a reload — a known, accepted limitation (ruled by
 * the architecture session 2026-08-23); the answer text itself is unaffected.
 * Portfolio-only: absent on in-lead turns by contract, and gated on the surface at
 * the call site too.
 */
const AnswerScopeLine: React.FC<AnswerScopeLineProps> = ({ answerScope, onPinCompany, disabled = false }) => {
    const covered = answerScope.covered ?? [];
    const excluded = answerScope.excluded ?? [];

    // The contract only emits answer_scope when one of the two is non-empty, but an
    // empty object would otherwise render a bare "Answering about:" with nothing
    // after it.
    if (!covered.length && !excluded.length) return null;

    return (
        <div className={styles.AnswerScope} data-testid="answer-scope-line">
            {covered.length > 0 && (
                <div className={styles.CoveredRow}>
                    <span className={styles.Prefix}>{GLOBAL_ASK_AICA_COPY.ANSWER_SCOPE_PREFIX}</span>
                    <span className={styles.Chips}>
                        {covered.map((company) =>
                            onPinCompany ? (
                                <button
                                    key={company.org_id}
                                    type="button"
                                    className={styles.Chip}
                                    onClick={() => onPinCompany(company.org_id)}
                                    disabled={disabled}
                                    title={GLOBAL_ASK_AICA_COPY.ANSWER_SCOPE_CHIP_TITLE(company.name)}
                                    data-testid="answer-scope-chip"
                                >
                                    {company.name}
                                </button>
                            ) : (
                                <span key={company.org_id} className={styles.ChipStatic} data-testid="answer-scope-chip-static">
                                    {company.name}
                                </span>
                            ),
                        )}
                    </span>
                </div>
            )}

            {excluded.map((item) => (
                // Keyed on written+reason: `written` alone can repeat across reasons,
                // and there is no id on an excluded name by definition.
                <p key={`${item.written}:${item.reason}`} className={styles.Excluded} data-testid="answer-scope-excluded">
                    {answerScopeExcludedCopy(item.written, item.reason)}
                </p>
            ))}
        </div>
    );
};

export default AnswerScopeLine;
